<template>
  <print v-if="isPrint" :form="form" :board="board" :projectId="projectId" :boardId="boardId" :postId="postId" />
  <div v-else>
    <!--상단 현재 상태를 표시한다-->
    <div class="overflow-x-auto">
      <status-step :steps="steps" :status="statusNumber" :width="'100%'" />
    </div>

    <!-- 허가 신청자 -->
    <field-select-module title="허가 신청자" v-model="form.stdInfo" :board="board" :is-print="isPrint" />

    <!--    작업 구분-->
    <div class="mt-28px">
      <h5 class="mb-4"><i class="dot"></i> 작업 구분</h5>
      <radio-group class="gap-12" flex v-model="form.작업구분" :items="['외부작업', '내부작업']" :readonly="board.readonly" />
    </div>

    <!--    작업 정보-->
    <div class="mt-28px">
      <h5 class="mb-4"><i class="dot"></i> 작업 정보</h5>
      <div v-if="form.작업구분 === '외부작업'" class="writeGridContainer">
        <div class="writeGridLineItem">
          <div>안전작업허가서</div>
          <div class="flex gap-2">
            <select845-button @select="loadRowData" />
          </div>
        </div>
      </div>
    </div>

    <!--    작업허가 -->
    <work-permit class="mt-4" v-model="form.작업허가" :board="board" :readonly="board.readonly || checkSelected845"
                 :is-print="isPrint" :use-extended-time="false" :funcSelectOption="onSelectWorkPermit"
                 :is-safety-manager="isSafetyManager"
                 @changeDate="findBusyUserList"/>

    <div class="mt-4">
      <div class="writeGridContainer">
        <div class="writeGridCol2">
          <div v-if="form.작업구분 === '외부작업'">업체명</div>
          <div v-else>부서(팀)명</div>
          <p v-if="isPrint" class="print-input">{{ form.작업정보.업체명.com_name }}</p>
          <company-selector v-else class="w-full" v-model="form.작업정보.업체명.id" :company-name="form.작업정보.업체명.com_name" :readonly="board.readonly || checkSelected845" @select="(company) => (form.작업정보.업체명 = company)" />
        </div>
        <div v-if="form.작업구분 === '외부작업'" class="writeGridCol2">
          <div>작업허가명</div>
          <p v-if="isPrint" class="print-input">{{ form.작업정보.작업허가명 }}</p>
          <ml-input v-else v-model="form.작업정보.작업허가명" :readonly="board.readonly || checkSelected845" class="w-full" />
        </div>
        <div v-if="form.작업구분 === '외부작업'" class="writeGridCol2">
          <div>허가번호</div>
          <p v-if="isPrint" class="print-input">{{ form.작업정보.허가번호 }}</p>
          <ml-input v-else v-model="form.작업정보.허가번호" readonly class="w-full" />
        </div>
        <div class="writeGridCol2">
          <div>작업장소</div>
          <div class="flex w-full">
            <region-selector v-model="form.작업정보.작업구역" :readonly="board.readonly || checkSelected845" />
          </div>
        </div>
        <div class="writeGridCol2">
          <div>장비번호(차량번호)</div>
          <p v-if="isPrint" class="print-input">{{ form.작업정보.장비번호 }}</p>
          <ml-input v-else v-model="form.작업정보.장비번호" :readonly="board.readonly || checkSelected845" class="w-full" />
        </div>
        <div class="writeGridCol2">
          <div>작업내용</div>
          <p v-if="isPrint" class="print-input">{{ form.작업정보.작업내용 }}</p>
          <ml-input v-else v-model="form.작업정보.작업내용" :readonly="board.readonly || checkSelected845" class="w-full" />
        </div>
      </div>
    </div>

    <!--    안전관계자(승인권자)-->
    <div class="mt-28px">
      <div class="flex flex-wrap justify-between items-center">
        <h5 class="mb-4"><i class="dot"></i> 안전관계자(승인권자)</h5>
        <div v-if="form.작업구분 === '외부작업'" class="mb-4 flex ml-auto">
          <vs-button class="mr-2" color="secondary" @click="addSafetyManager">공사업체 인원 추가</vs-button>
          <vs-button color="secondary" type="border" :disabled="form.안전관계자.공사업체.length === 1" @click="removeSafetyManager">마지막 행삭제</vs-button>
        </div>
      </div>

      <div class="writeGridContainer">
        <div>
          <div>동국제강</div>
          <p v-if="isPrint" class="print-input">{{ form.안전관계자.동국제강.name }}</p>
          <member-selector
            v-else
            popup-type="user"
            class="w-full"
            v-model="form.안전관계자.동국제강.id"
            :member-name="form.안전관계자.동국제강.name"
            :readonly="board.readonly || checkSelected845"
            @select="(member) => onSelectMember(form.안전관계자.동국제강, member)"
          />
        </div>
        <div>
          <div>전화번호</div>
          <p v-if="isPrint" class="print-input">{{ form.안전관계자.동국제강.phone }}</p>
          <ml-input v-else class="w-full" :value="formatPhone(form.안전관계자.동국제강.phone)" readonly />
        </div>
      </div>

      <div v-if="form.작업구분 === '외부작업'" v-for="(contractor, idx) in form.안전관계자.공사업체" :key="idx" class="writeGridContainer" :class="{ 'border-top': isMobile }" style="margin-bottom: 0.625rem; padding-top: 0.625rem">
        <div>
          <div>공사업체</div>
          <p v-if="isPrint" class="print-input">{{ contractor.name }}</p>
          <member-selector v-else popup-type="user" class="w-full" v-model="contractor.id" :member-name="contractor.name" :readonly="board.readonly || checkSelected845" @select="(member) => onSelectMember(contractor, member)" />
        </div>
        <div>
          <div>전화번호</div>
          <p v-if="isPrint" class="print-input">{{ contractor.phone }}</p>
          <ml-input v-else class="w-full" :value="formatPhone(contractor.phone)" readonly />
        </div>
      </div>

      <div class="writeGridContainer">
        <div>
          <div>확인자(안전환경팀, 필요시)</div>
          <p v-if="isPrint" class="print-input">{{ form.안전관계자.확인자유무 }}</p>
          <div v-else class="w-full flex-1 flex gap-4">
            <radio-group
              v-model="form.안전관계자.확인자유무"
              flex
              :readonly="board.readonly || !isSafetyManager"
              :items="[
                { label: 'Y', value: 'y' },
                { label: 'N', value: 'n' },
              ]"
            />
          </div>
        </div>
        <div v-if="form.안전관계자.확인자유무 === 'y'">
          <div>확인자</div>
          <p v-if="isPrint" class="print-input">{{ form.안전관계자.확인자.name }}</p>
          <member-selector v-else class="w-full" popup-type="user" :member-name="form.안전관계자.확인자.name" v-model="form.안전관계자.확인자.id" @select="(member) => onSelectMember(form.안전관계자.확인자, member)" />
        </div>
        <div v-if="form.안전관계자.확인자유무 === 'y'">
          <div>서명</div>
          <sign-field v-if="!isPrint && !board.readonly" v-model="form.안전관계자.확인자서명.sign" class="w-full flex-1 p-1" />
          <div v-else class="w-full flex-1 p-1">
            <img v-if="form.안전관계자.확인자서명.sign" :src="form.안전관계자.확인자서명.sign" width="100%" alt="Signature" class="signatureImg" />
            <div v-else class="signatureImg"></div>
          </div>
        </div>
      </div>
    </div>

    <!-- 장비유형 -->
    <work-table979 v-model="form.장비유형" :readonly="board.readonly" :status="statusNumber" :is-print="isPrint"
                   :board="board" :status-number="status"/>

    <!--운전자 정보-->
    <worker-list979
      title="운전자 정보"
      v-model="form.운전자정보"
      :startDate="form.작업허가.작업시작일시"
      :endDate="form.작업허가.작업종료일시"
      :isPrint="isPrint"
      :qr-list="qrList"
      :refreshQrList="refreshQrList"
      @select="findBusyUserList"
    />

    <div class="mt-4">
      <div class="writeGridContainer">
        <div>
          <div>DKS 발주부서장 확인서</div>
          <file-uploader v-model="form.DKS발주부서장확인서" :readonly="board.readonly" class="w-full" />
        </div>
      </div>
    </div>

    <!--    사진대지-->
    <PhotoUploadGridModule :row-count="2" :grid-count="2" v-model="form.photoList" :is-print="isPrint" :readonly="board.readonly || !isSafetyManager" section-title="작업 전 사진" placeholder="00센터 신축현장 점검" />
    <PhotoUploadGridModule :row-count="2" :grid-count="2" v-model="form.photoList2" :is-print="isPrint" :readonly="board.readonly || !isSafetyManager" section-title="작업 후 사진" placeholder="00센터 신축현장 점검" />

    <file-upload-module sub-title="※ 작업계획서, 보험증권" id="fileList" class="w-full mt-4" v-model="form.fileList" :is-print="isPrint" :readonly="board.readonly"></file-upload-module>
  </div>
</template>

<script>
import { formatPhone } from "@/util/DataUtil";
import FileUploadModule from "@/views/modules/FileUploadModule";
import FieldSelectModule from "@/views/modules/FieldSelectModule";
import WriteGridContainer from "@/components/WriteGridContainer.vue";
import DateSelector from "@/components/selector/DateSelector.vue";
import SignField from "@/components/SignField.vue";
import TimeSelector from "@/components/selector/TimeSelector.vue";
import TableCustom from "@/components/TableCustom.vue";
import TableInspectMerge from "@/components/TableInspectMerge.vue";
import WorkerList979 from "./WorkerList979.vue";
import WorkTable979 from "./WorkTable979/WorkTable979.vue";
import StatusStep from "@/components/StatusStep.vue";
import RadioGroup from "@/components/radio/RadioGroup.vue";
import Select845Button from "@/views/boards/single-page/845/Select845Button.vue";
import * as inspectData from "./inspectData";
import WorkPermit from "@/components/work-permit/WorkPermit.vue";
import Print from "./Print.vue";
import MemberSelector from "@/components/selector/MemberSelector.vue";
import CompanySelector from "@/components/selector/CompanySelector.vue";
import RegionSelector from "@/components/selector/RegionSelector.vue";
import WorkerList976 from "@/views/boards/single-page/976/WorkerList976.vue";
import FileUploader from "@/components/FileUploader.vue";

export default {
  components: {
    WorkerList976,
    RegionSelector,
    CompanySelector,
    MemberSelector,
    Print,
    WorkPermit,
    Select845Button,
    RadioGroup,
    StatusStep,
    WorkTable979,
    WorkerList979,
    TableInspectMerge,
    TableCustom,
    TimeSelector,
    SignField,
    DateSelector,
    WriteGridContainer,
    FieldSelectModule,
    FileUploadModule,
    FileUploader,
  },
  props: {
    isPrint: Boolean,
    board: Object,
    value: Object,
    header: Object,
    post: Object, // 게시글 전체 정보
  },
  computed: {
    projectId() {
      return parseInt(this.$route.params.proj_id);
    },
    postId() {
      return parseInt(this.$route.params.postId);
    },
    boardId() {
      return parseInt(this.$route.params.boardId);
    },
    qrList() {
      return this.post.qr_reads;
    },
    status() {
      const workStart = this.form.작업허가 && this.form.작업허가.작업승인 ? this.form.작업허가.작업승인.상태 === "승인" : "";
      const workEnd = this.form.작업허가 && this.form.작업허가.작업종료 ? this.form.작업허가.작업종료.상태 === "승인" : "";
      if (workEnd) return this.steps.indexOf("작업완료");
      if (workStart) return this.steps.indexOf("작업중");
      return this.steps.indexOf("등록");
    },
    statusNumber() {
      const status = this.status;
      const workers = this.form.운전자정보;
      if (workers.length > 0 && workers.every((worker) => worker.출문시간)) return Math.max(status, this.steps.indexOf("출문"));
      if (workers.some((worker) => worker.입문시간)) return Math.max(status, this.steps.indexOf("입문"));
      return status;
    },
    isMobile() {
      return this.$viewport.isMobile;
    },
    checkSelected845() {
      if (this.form.작업구분 === "내부작업") {
        return false;
      }
      return this.form.안전작업허가서_id === 0;
    },
  },
  data() {
    return {
      steps: ["등록", "입문", "작업중", "작업완료", "출문"],
      busyUserList: [], // 이미 작업중인 사용자 목록
      restrictedUsers: [], // 출입제한 사용자 목록
      showListPopup: false,
      form: {
        stdInfo: {},
        users: [], // qr code 로 읽은 사용자 정보 { id: number, type?: string }
        photoList: [],
        photoList2: [],
        fileList: [],

        안전작업허가서_id: 0,
        상태: "",
        작업구분: "외부작업",
        작업허가: {},
        작업정보: {
          작업시작날짜: "",
          작업완료날짜: "",
          업체명: { id: "", com_name: "" }, // 업체명 = 부서(팀)명
          작업허가명: "",
          허가번호: "",
          작업구역: {},
          분류: "",
          장비번호: "",
        },
        안전관계자: {
          동국제강: { id: "", name: "", phone: "" },
          공사업체: [{ id: "", name: "", phone: "" }],
          확인자유무: "n",
          확인자: { id: "", name: "" },
          확인자서명: { sign: "" },
        },

        장비유형: {
          유형: ["지게차", "이동식크레인", "고소작업차", "고소작업대", "굴삭기", "기타"],
          선택: [],
          지게차: {
            inspectList: inspectData.inspectTable_1,
          },
          이동식크레인: {
            inspectList: inspectData.inspectTable_2,
          },
          고소작업차: {
            inspectList: inspectData.inspectTable_3,
          },
          고소작업대: {
            inspectList: inspectData.inspectTable_4,
          },
          굴삭기: {
            inspectList: inspectData.inspectTable_5,
          },
          기타: {
            note: "",
          },
        },
        운전자정보: [],
        DKS발주부서장확인서: {},
      },
      isSafetyManager: false,
    };
  },
  watch: {
    "value": {
      immediate: true,
      deep: true,
      handler(value) {
        const keys = Object.keys(this.form);
        for (const key of keys) {
          if (value[key]) {
            if (key === "작업허가" && this.form[key]) {
              // 기존 승인 정보 백업
              let 작업승인_정보 = {};
              let 연장승인_정보 = {};
              let 작업종료_정보 = {};

              // 작업승인 정보 백업
              if (this.form[key].작업승인 && this.form[key].작업승인.승인자) {
                작업승인_정보 = {
                  승인자: JSON.parse(JSON.stringify(this.form[key].작업승인.승인자)),
                  승인일시: this.form[key].작업승인.승인일시
                };
              }

              // 연장승인 정보 백업
              if (this.form[key].연장승인 && this.form[key].연장승인.승인자) {
                연장승인_정보 = {
                  승인자: JSON.parse(JSON.stringify(this.form[key].연장승인.승인자)),
                  승인일시: this.form[key].연장승인.승인일시
                };
              }

              // 작업종료 정보 백업
              if (this.form[key].작업종료 && this.form[key].작업종료.승인자) {
                작업종료_정보 = {
                  승인자: JSON.parse(JSON.stringify(this.form[key].작업종료.승인자)),
                  승인일시: this.form[key].작업종료.승인일시
                };
              }

              // 새 데이터로 업데이트
              this.form[key] = value[key];

              // 작업승인 정보 복원 (필요한 경우)
              if (작업승인_정보.승인일시 && 작업승인_정보.승인자) {
                if (!this.form[key].작업승인 || !this.form[key].작업승인.승인자 ||
                  Object.keys(this.form[key].작업승인.승인자).length === 0) {
                  // 승인자 정보가 없거나 비어있는 경우에만 복원
                  if (!this.form[key].작업승인) {
                    this.form[key].작업승인 = {};
                  }
                  this.form[key].작업승인.승인자 = 작업승인_정보.승인자;
                }
              }

              // 연장승인 정보 복원 (필요한 경우)
              if (연장승인_정보.승인일시 && 연장승인_정보.승인자) {
                if (!this.form[key].연장승인 || !this.form[key].연장승인.승인자 ||
                  Object.keys(this.form[key].연장승인.승인자).length === 0) {
                  if (!this.form[key].연장승인) {
                    this.form[key].연장승인 = {};
                  }
                  this.form[key].연장승인.승인자 = 연장승인_정보.승인자;
                }
              }

              // 작업종료 정보 복원 (필요한 경우)
              if (작업종료_정보.승인일시 && 작업종료_정보.승인자) {
                if (!this.form[key].작업종료 || !this.form[key].작업종료.승인자 ||
                  Object.keys(this.form[key].작업종료.승인자).length === 0) {
                  if (!this.form[key].작업종료) {
                    this.form[key].작업종료 = {};
                  }
                  this.form[key].작업종료.승인자 = 작업종료_정보.승인자;
                }
              }
            } else {
              this.form[key] = value[key];
            }
          }
        }

        this.form.상태 = this.status;
      },
    },
    "form": {
      immediate: true,
      deep: true,
      handler(value) {
        value._etc = {
          구분: value.작업구분,
          작업허가명: value.작업정보.작업허가명,
          허가번호: value.작업정보.허가번호,
          관리감독자: value.안전관계자.동국제강,
          작업장소: value.작업정보.작업구역,
          작업내용: value.작업정보.작업내용,
        };
        this.$emit("input", value);
      },
    },
    "form.운전자정보": {
      deep: true,
      handler(value) {

        // busyUserList 에 등록되어 있는 사용자는 작업자로 등록할 수 없다.
        for (let i = 0; i < value.length; i++) {
          if (this.busyUserList.includes(parseInt(`${value[i].userId}`))) {
            value[i].error = "이미 작업중인 사용자입니다.";
          } else if (this.restrictedUsers.includes(parseInt(`${value[i].userId}`))) {
            value[i].error = "출입이 제한된 사용자입니다.";
          } else {
            delete value[i].error;
          }
        }

        this.form.users = value.map((worker) => ({ id: worker.userId, type: "qr" }));
      },
    },
  },
  created() {
    // 현재 사용자가 관리감독자인지 확인한다
    this.checkSafetyManger();
  },
  methods: {
    formatPhone,

    clickPageTop() {
      const workPermitElement = document.getElementById('workPermit');
      if (workPermitElement) {
        const elementPosition = workPermitElement.getBoundingClientRect().top;

        // 현재 스크롤 위치가 workPermit 요소 근처인지 확인 (헤더 높이 + 여유 공간 고려)
        const isNearWorkPermit = Math.abs(elementPosition - 100) < 150;

        if (isNearWorkPermit) {
          // 이미 workPermit 근처에 있으면 최상단으로 스크롤
          window.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
        } else {
          // workPermit 요소로 스크롤 (100px 위로)
          const offsetPosition = elementPosition + window.pageYOffset - 100;
          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      }
    },
    setErrorMsg () {
      let count = 0;

      // busyUserList 에 등록되어 있는 사용자는 작업자로 등록할 수 없다.
      const errBusy = "이미 작업중인 사용자입니다."
      for (let i = 0; i < this.form.운전자정보.length; i++) {
        if (this.busyUserList.includes(parseInt(`${this.form.운전자정보[i].userId}`))) {
          if (!this.form.운전자정보[i].error) {
            this.form.운전자정보[i].error = errBusy;
            count++;
          }
        } else if (this.form.운전자정보[i].error === errBusy) {
          delete this.form.운전자정보[i].error;
        }
      }

      // busyUserList 에 등록되어 있는 사용자는 작업자로 등록할 수 없다.
      const errRes = "출입이 제한된 사용자입니다."
      for (let i = 0; i < this.form.운전자정보.length; i++) {
        if (this.restrictedUsers.includes(parseInt(`${this.form.운전자정보[i].userId}`))) {
          if (!this.form.운전자정보[i].error) {
            this.form.운전자정보[i].error = errRes;
            count++;
          }
        } else if (this.form.운전자정보[i].error === errRes) {
          delete this.form.운전자정보[i].error;
        }
      }


      if (count) {
        this.form.운전자정보 = [...this.form.운전자정보];
      }
    },
    async validate() {
      const 운전자Ids = this.form.운전자정보.map((worker) => worker.userId);
      const response = await this.$store.dispatch("board/LOAD_POST_LIST", {
        projectId: this.projectId,
        boardId: 1043,
        page: 1,
        perPage: Math.max(운전자Ids.length, 1),
        conditions: [
          {key: "body.작업자정보.작업자.id", operator: "IN", value: 운전자Ids},
          {key: "body.출입제한기간.sDate", operator: "<=n", value: `${new Date().format('yyyy-MM-dd')}`},
          {key: "body.출입제한기간.eDate", operator: ">=n", value: `${new Date().format('yyyy-MM-dd')}`}
        ],
      });
      const 검색된_출입제한_작업자_list = response.data
      const 검색된_출입제한_작업자_ids = 검색된_출입제한_작업자_list
        .filter(item => {
          const 제한사업장 = JSON.parse(item.post_content).body.제한사업장 || [];
          // 안전작업허가서(845)를 선택하면 허가 신청자의 사업장이 함께 선택되므로, 허가 신청자의 사업장명으로 비교함
          return 제한사업장.length === 0 || 제한사업장.includes("전사") || 제한사업장.includes(this.form.stdInfo.field_name);
        })
        .map(item => {
          const body = JSON.parse(item.post_content).body
          return body.작업자정보.작업자.id
        })
      this.restrictedUsers = 검색된_출입제한_작업자_ids;
      this.setErrorMsg();

      if (검색된_출입제한_작업자_ids.length > 0) {
        return "<p style='font-size: 24px; font-weight: 500; text-align: center; margin-bottom: 1rem;'>출입이 제한된 인원이 있습니다.</p><p style='text-align: center;'>운전자 정보를 확인해주세요.</p>"
      }

      if (!this.form.작업정보.업체명 || !this.form.작업정보.업체명.id) {
        return "업체명을 입력해주세요.";
      }
      if (this.form.작업구분 === "외부작업" && !this.form.작업정보.작업허가명) {
        return "작업허가명을 입력해주세요.";
      }
      if (!this.form.작업정보.작업구역 || !(this.form.작업정보.작업구역.region.id > 0 && this.form.작업정보.작업구역.zone.id > 0)) {
        return "작업장소를 선택해주세요.";
      }
      if (!this.form.작업정보.작업내용) {
        return "작업내용을 입력해주세요.";
      }

      for (const type of this.form.장비유형.선택) {
        if (type === "기타") continue; // Skip "기타" as it doesn't have 점검자확인자

        const equipment = this.form.장비유형[type];
        if (!equipment || !equipment.점검자 || !equipment.점검자서명) {
          return "점검자를 입력해주세요.";
        }
        if (!equipment.확인자 || !equipment.확인자서명) {
          return "확인자를 입력해주세요.";
        }
      }
    },

    // 작업허가서에 등록되어 있는 작업자 중 현재 작업시간에 작업이 불가능한 작업자를 찾는다.
    async findBusyUserList() {
      if (!(this.form.작업허가.작업시작일시 && this.form.작업허가.작업시간.시작 && this.form.작업허가.작업시간.종료)) {
        return;
      }

      const conditions = [];
      // 작업시작일시가 오늘인 게시글을 불러온다
      const today = new Date(this.form.작업허가.작업시작일시);
      conditions.push({ key: "body.작업허가.작업시작일시", operator: ">=", value: today.format("yyyy-MM-dd") });
      conditions.push({ key: "body.작업허가.작업시작일시", operator: "<", value: today.addDays(1).format("yyyy-MM-dd") });
      // 작업시간이 겹친 게시글을 불러온다.
      conditions.push({ key: "body.작업허가.작업시간.시작", operator: "<", value: this.form.작업허가.작업시간.종료 });
      conditions.push({ key: "body.작업허가.작업시간.종료", operator: ">", value: this.form.작업허가.작업시간.시작 });

      const options = {
        projectId: this.projectId,
        postId: this.postId,
        page: 1,
        perPage: 50,
        conditions,
      };
      // 976 외부공사·작업
      // 981 내부작업(직영)
      // 979 일 장비사용계획서
      this.loading(true);

      try {
        const boardIds = [976, 981, 979];
        const promises = boardIds.map(boardId => this._loadDefragmentation({ ...options, boardId }));
        const [list976, list981, list979] = await Promise.all(promises);

        const userIds = [];
        for (let i = 0; i < list976.length; i++) {
          const body = JSON.parse(list976[i].post_content).body;
          userIds.push(...body.작업자내역.map((worker) => worker.userId));
        }
        for (let i = 0; i < list981.length; i++) {
          const body = JSON.parse(list981[i].post_content).body;
          userIds.push(...body.작업자내역.map((worker) => worker.userId));
        }
        for (let i = 0; i < list979.length; i++) {
          const body = JSON.parse(list979[i].post_content).body;
          userIds.push(...body.운전자정보.map((worker) => worker.userId));
        }

        this.busyUserList = [...new Set(userIds)];
        this.setErrorMsg();
      } catch (error) {
        console.error("작업자 정보 조회 중 오류 발생:", error);
      } finally {
        this.loading(false);
      }
    },
    async _loadDefragmentation(options) {
      const response = await this.$store.dispatch("board/LOAD_POST_LIST", options);
      const list = [];
      for (let i = 0; i < response.data.length; i++) {
        // 현재 게시글은 제외한다.
        if (response.data[i].id === options.postId) {
          continue;
        }
        list.push(response.data[i]);
      }
      if (response.current_page !== response.last_page) {
        options.page = response.current_page + 1;
        list.push(...(await this._loadDefragmentation(options)));
      }
      return list;
    },
    loading(open) {
      const loadingDialog = new CustomEvent("loadingDialog", {
        detail: {
          open,
        },
      });
      window.dispatchEvent(loadingDialog);
    },
    refreshQrList() {
      if (this.boardId && this.postId) {
        this.$store
          .dispatch("board/LOAD_POST", {
            boardId: this.boardId,
            postId: this.postId,
          })
          .then((post) => {
            this.post.qr_reads = post.qr_reads;
          });
      }
    },
    addSafetyManager() {
      this.form.안전관계자.공사업체.push({ 이름: "", 전화번호: "" });
    },
    removeSafetyManager() {
      if (this.form.안전관계자.공사업체.length < 2) return;

      this.form.안전관계자.공사업체.pop();
    },
    loadRowData(data) {
      if (data && data.post_content) {
        this.form.안전작업허가서_id = data.id;

        const content = JSON.parse(data.post_content);
        const body = content.body;

        this.form.stdInfo.field_id = body.stdInfo.field_id;
        this.form.stdInfo.field_name = body.stdInfo.field_name;

        const 업체명 = body.안전작업허가서정보.작업업체명;
        const 작업허가명 = body.안전작업허가서정보.공사작업명;
        const 허가번호 = body.stdInfo.허가번호; //(?)
        const 작업내용 = body.안전작업허가서정보.세부작업내용;
        const 작업장소 = body.안전작업허가서정보._작업구역;
        const 동국제강 = body.안전관계자.동국제강;
        const 공사업체 = body.안전관계자.공사업체;

        this.form.작업정보.업체명 = 업체명;
        this.form.작업정보.작업허가명 = 작업허가명;
        this.form.작업정보.허가번호 = 허가번호;
        this.form.작업정보.작업구역 = 작업장소;
        this.form.작업정보.작업내용 = 작업내용;
        this.form.안전관계자.동국제강 = 동국제강;
        this.form.안전관계자.공사업체 = 공사업체;

        // 65세 이상 관련 증빙서류
        // this.form.DKS발주부서장확인서 = body.공사인원.DKS발주부서장확인서
      }

      this.findBusyUserList();
    },
    onSelectMember(member, selectedMember) {
      const keys = Object.keys(selectedMember);
      for (const key of keys) {
        member[key] = selectedMember[key];
      }
    },

    onSelectWorkPermit(type, status) {
      if (type === "작업승인" && status === "승인") {
        // photoList 에 사진을 첨부해야 한다.
        const photoList = this.form.photoList.filter((photo) => photo.id);
        if (photoList.length === 0) {
          return {
            error: "작업 전 사진을 첨부해주세요.",
          };
        }
      }

      if (type === "작업종료" && status === "승인") {
        // photoList2 에 사진을 첨부해야 한다.
        const photoList2 = this.form.photoList2.filter((photo) => photo.id);
        if (photoList2.length === 0) {
          return {
            error: "작업 후 사진을 첨부해주세요.",
          };
        }
      }

      return true;
    },

    // 복사하기 기능을 사용하여, 복사된 게시글의 내용을 수정할 때 호출
    // (URL query 에 _cp_pid 가 있을 경우 호출)
    // watch 로 처리하지 않은 이유는, watch 로 처리하면, 필요 없는 데이터를 초기화 하는 과정에서 불필요하게 화면을 갱신하기 때문 (무한반복하는 경우도 있음)
    cpPostContent(contents) {
      const body = contents.body;

      // 복사 제외 처리
      if (body) {
        // stdInfo 제외
        if (body.stdInfo) delete body.stdInfo

        // 작업허가 - 대기상태로 변경
        if (body.작업허가) {
          body.작업허가 = {
            작업시작일시: body.작업허가.작업시작일시,
            작업종료일시: body.작업허가.작업종료일시,
            연장종료일시: "",
            작업시간: {
              시작: body.작업허가.작업시간.시작,
              종료: body.작업허가.작업시간.종료,
              연장: "",
              error: "",
            },
            작업승인: {
              상태: "대기",
              승인자: {
                부서: "",
                소속: "",
                직책: "",
                성명: "",
                서명: { sign: "" },
              },
              승인일시: "",
              error: "",
            },
            연장승인: {
              상태: "없음",
              승인자: {
                부서: "",
                소속: "",
                직책: "",
                성명: "",
                서명: { sign: "" },
              },
              승인일시: "",
              error: "",
            },
            작업종료: {
              상태: "대기",
              승인자: {
                부서: "",
                소속: "",
                직책: "",
                성명: "",
                서명: { sign: "" },
              },
              승인일시: "",
              error: "",
            },
          }
        }

        // 작업 전/후 사진
        if (body.photoList) delete body.photoList
        if (body.photoList2) delete body.photoList2
      }

      contents.body = {
        ...this.form,
        ...body,
      };
      return contents;
    },

    // 상단 버튼 설정
    setNormalBoardButtons(template) {
      const saveFunc = template.save.emits.click;
      template.save.shouldShow.onlyWriter = false;
      template.save.emits.click = (func) => {

        if (!this.form.stdInfo.field_id) {
          this.$vs.notify({
            title: "사업장명 선택",
            text: "허가 신청자의 사업장명을 선택해주세요.",
            color: "danger",
            position: "top-right",
          });
          return;
        }

        // form.작업자내역
        for (let i = 0; i < this.form.운전자정보.length; i++) {
          const worker = this.form.운전자정보[i];

          if (worker.error) {
            this.$vs.notify({
              title: "운전자 오류",
              text: "이미 작업중인 사용자가 있습니다.",
              color: "danger",
              position: "top-right",
            });
            return;
          }
        }

        // 저장
        saveFunc(func);
      };
      return [template.print, template.delete, template.move, template.save, template.copy];
    },

    /**
     * 현재 사용자가 안전보건관리자로 지정되어 있는지 확인한다
     */
    async checkSafetyManger() {
      const boardIds = [738, 996, 997, 998, 999, 1000, 1038];
      const fields = ['body.관리감독자정보.사번', 'body.subInfo.사번'];
      const response = await this.$store.dispatch('board/CHECK_SAFETY_MANAGER', {
        boardIds,
        fields,
      });

      if (response) {
        this.isSafetyManager = response.isSafetyManager || false;
        return response.isSafetyManager || false;
      }
      return false;
    },
  },
};
</script>
